import { useState } from 'react'
import {
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  Mail,
  Pencil,
  ReceiptText,
  Send,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import { capitalize } from 'lodash'
import { useDownloadCashReceiptMutation, useSendRegistrationRequestMutation } from '../hooks'
import { UploadDepositReceiptForm } from './UploadDepositReceiptForm'
import { DeleteDonation } from './DeleteDonation'
import { DonationForm } from './DonationForm'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/custom/modal'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { useUsersStore } from '@/features/users/store/usersStore'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/timeZoneUtils'
import { useAuthStore } from '@/features/auth/store/authStore'
import ExportDialog from '@/features/receipts/components/ExportDialog'
import { usePermissions } from '@/utils/permissions'

interface DonationDetailDialogProps {
  donationId: string
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
  onUploadPosted?: (uploadedId: string) => void
}

export function DonationDetailDialog({
  donationId,
  trigger,
  open,
  onClose,
  onUploadPosted,
}: DonationDetailDialogProps) {
  const permission = usePermissions()

  const [openDialog, setOpenDialog] = useState({
    upload: false,
    edit: false,
    delete: false,
    view: false,
  })
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null)

  const donations = useDonationsStore((s) => s.donations)
  const members = useMembersStore((s) => s.members)
  const churches = useChurchesStore((s) => s.churches)
  const users = useUsersStore((s) => s.users)
  const activeRole = useAuthStore((s) => s.activeRole)

  const donation = donations.find((d) => d._id === donationId)
  const donor = members.find((m) => m._id === donation?.donorId)
  const church = churches.find((c) => c._id === donation?.churchId)
  const sentByUser = users.find((u) => u._id === donation?.sentToMemberBy)

  const {
    mutateAsync: downloadReceiptMutation,
    isPending: downloadingReceipt,
  } = useDownloadCashReceiptMutation()

  const {
    mutate: sendRegistrationRequest,
    isPending: sendingRegistrationRequest,
  } = useSendRegistrationRequestMutation()

  const handleViewReceipt = async () => {
    try {
      const result = await downloadReceiptMutation(donationId)
      if (result.downloadUrl) {
        setReceiptUrl(result.downloadUrl)
        setOpenDialog((prev) => ({ ...prev, view: true }))
      }
    } catch (error) {
      console.error('Failed to get receipt URL:', error)
    }
  }

  if (!donation) return null

  const handleClose = () => {
    onClose?.()
    setOpenDialog({ upload: false, edit: false, delete: false, view: false })
  }

  const isUnmatchedSender =
    donation.source === 'etransfer_auto' && !donation.donorId

  const canEmailSender =
    isUnmatchedSender &&
    (activeRole === 'SYSTEM_ADMIN' || activeRole === 'DEVELOPER')

  const footerActions = activeRole !== 'PASTOR' && (
    <div className="flex flex-wrap gap-2 justify-end mt-6 pt-4 border-t">
      {canEmailSender && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => sendRegistrationRequest(donation._id)}
          disabled={sendingRegistrationRequest || !donation.emailFrom}
          className="flex items-center gap-2"
        >
          <Mail className="h-4 w-4" />
          {donation.registrationEmailSent
            ? 'Resend Registration Request'
            : 'Email Sender'}
        </Button>
      )}
      {!donation.isRegistrationFee && (
        <ExportDialog
          type="donation"
          memberId={donation.donorId}
          id={donation._id}
          trigger={
            <Button variant="outline" size="sm">
              <Send className="mr-2 h-4 w-4" />{' '}
              {`${!donation.sentToMember ? 'Send' : 'Resend'} Receipt`}
            </Button>
          }
        />
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpenDialog((prev) => ({ ...prev, edit: true }))}
        className="flex items-center gap-2"
      >
        <Pencil className="h-4 w-4" />
        Edit
      </Button>
      {permission.canDeleteDonation() && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setOpenDialog((prev) => ({ ...prev, delete: true }))}
          className="flex items-center gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      )}
      {donation.method === 'cash' && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenDialog((prev) => ({ ...prev, upload: true }))}
            className="flex items-center gap-2"
          >
            <UploadCloud className="h-4 w-4" />
            {donation.deposited
              ? 'Replace Deposit Receipt'
              : 'Upload Deposit Receipt'}
          </Button>
          {donation.deposited && donation.depositReceiptDrivePath && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleViewReceipt}
              disabled={downloadingReceipt}
            >
              <ReceiptText className="h-4 w-4" />
              {downloadingReceipt ? 'Loading...' : 'View Deposit Receipt'}
            </Button>
          )}
        </>
      )}
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={`Donation Details - ${donation.receiptNumber}`}
      contentClassName="w-full md:min-w-5xl max-w-8xl h-fit"
    >
      <div className="space-y-4">
        {donation.isRegistrationFee && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-4 py-2 rounded-lg text-sm font-medium">
            <span className="inline-block w-2 h-2 rounded-full bg-orange-500 shrink-0" />
            Registration Fee — no receipt will be issued or included in the
            annual summary.
          </div>
        )}
        {/* Donation Overview */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="text-2xl font-semibold">
                  {formatCurrency(donation.amount)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-red-700" />
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="text-lg font-semibold">
                  {formatDate(donation.date, 'MMMM D, YYYY')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Donation Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="space-y-3">
              <div>
                <span className="font-medium text-gray-600">
                  Receipt Number:
                </span>
                <p className="text-gray-900">{donation.receiptNumber}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">
                  Payment Method:
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <CreditCard className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-900">
                    {capitalize(donation.method)}
                  </span>
                </div>
              </div>
              {donation.method === 'cash' &&
                donation.deposited &&
                donation.depositDate && (
                  <div>
                    <span className="font-medium text-gray-600">
                      Deposited:
                    </span>
                    <p className="text-gray-900">
                      {formatDate(donation.depositDate, 'MMMM D, YYYY')}
                    </p>
                  </div>
                )}
              {donation.donationRef && (
                <div>
                  <span className="font-medium text-gray-600">
                    Reference Number:
                  </span>
                  <p className="text-gray-900">{donation.donationRef}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="space-y-3">
              <div>
                <span className="font-medium text-gray-600">Church:</span>
                <p className="text-gray-900">
                  {church ? church.name : 'Unassigned Church'}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Donor:</span>
                <p className="text-gray-900">{donor?.name || 'Unknown'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Unmatched Sender Information */}
        {isUnmatchedSender && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Unmatched Sender Information
            </h3>
            <div className="space-y-1 text-sm">
              <p className="text-amber-900">
                <span className="font-medium">Email: </span>
                {donation.emailFrom}
              </p>
              {donation.emailSubject && (
                <p className="text-amber-900">
                  <span className="font-medium">Subject: </span>
                  {donation.emailSubject}
                </p>
              )}
              {donation.registrationEmailSent && donation.registrationEmailSentAt && (
                <p className="text-green-700 mt-2">
                  <span className="font-medium">Registration email sent: </span>
                  {formatDate(
                    donation.registrationEmailSentAt,
                    'MMMM D, YYYY [at] h:mm A',
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Receipt Information */}
        {donation.sentToMember && (
          <div>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Receipt Sent</span>
              </div>
              <div className="space-y-1 text-sm">
                {sentByUser && (
                  <p className="text-green-700">
                    <span className="font-medium">Sent by:</span>{' '}
                    {sentByUser.name}
                  </p>
                )}
                <p className="text-green-700">
                  <span className="font-medium">Sent on:</span>{' '}
                  {donation.sentToMemberAt &&
                    formatDate(
                      donation.sentToMemberAt,
                      'MMMM D, YYYY [at] h:mm A',
                    )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {donation.notes && (
          <div>
            <h3 className="text-lg font-semibold">Notes</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-900">{donation.notes}</p>
            </div>
          </div>
        )}

        {/* Actions Footer */}
        {footerActions}
      </div>

      {/* Modals for actions */}
      {openDialog.view && receiptUrl && (
        <Modal
          open={openDialog.view}
          onClose={() => {
            setOpenDialog((prev) => ({ ...prev, view: false }))
            setReceiptUrl(null)
          }}
          title="Deposit Receipt"
          contentClassName="w-full max-w-4xl h-[80vh]"
        >
          <div className="w-full h-full bg-neutral-100 rounded-md overflow-hidden min-h-[50vh]">
            <object
              data={receiptUrl}
              className="w-full h-full border-0"
              aria-label="Receipt Viewer"
            />
          </div>
        </Modal>
      )}
      {openDialog.edit && (
        <DonationForm
          id={donation._id}
          type="edit"
          open={openDialog.edit}
          onClose={() => setOpenDialog((prev) => ({ ...prev, edit: false }))}
        />
      )}
      {openDialog.delete && (
        <DeleteDonation
          id={donation._id}
          open={openDialog.delete}
          onClose={() => setOpenDialog((prev) => ({ ...prev, delete: false }))}
        />
      )}
      {openDialog.upload && (
        <UploadDepositReceiptForm
          donationId={donation._id}
          open={openDialog.upload}
          onClose={() => setOpenDialog((prev) => ({ ...prev, upload: false }))}
          onUploaded={(id) => {
            onUploadPosted?.(id)
            setOpenDialog((prev) => ({ ...prev, upload: false }))
          }}
        />
      )}
    </Modal>
  )
}
